import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Box } from '@mui/material';
import GppBadRoundedIcon from '@mui/icons-material/GppBadRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import DnsRoundedIcon from '@mui/icons-material/DnsRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import SecurityScoreGauge from './components/SecurityScoreGauge';
import SeverityBreakdown from './components/SeverityBreakdown';
import ThreatTimeline from './components/ThreatTimeline';
import IncidentsPanel from './components/IncidentsPanel';
import LiveAttackFeed from './components/LiveAttackFeed';
import { loadDashboard } from './dashboardSlice';
import { tokens } from '../../theme/theme';

export default function DashboardPage() {
  const dispatch = useDispatch();
  const { status, summary, score, severityBreakdown, timeline, incidents, liveFeed } = useSelector((s) => s.dashboard);

  useEffect(() => {
    dispatch(loadDashboard());
  }, [dispatch]);

  const criticalCount = severityBreakdown.find((s) => s.key === 'critical')?.value ?? 0;
  const threatEventsLast24h = summary?.threatEventsLast24h ?? 0;
  const activeAssets = summary?.monitoredAssets ?? 0;
  const openVulnerabilities = summary?.openVulnerabilities ?? 0;
  const criticalOpenVulnerabilities = summary?.criticalOpenVulnerabilities ?? 0;

  return (
    <Box>
      <PageHeader
        title="Security Overview"
        subtitle="Real-time posture across all monitored assets and clients"
      />

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Critical Threats" value={criticalCount} icon={GppBadRoundedIcon} accent={tokens.severity.critical} delta="actionable findings" deltaGood={criticalCount === 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Threat Events (24h)" value={threatEventsLast24h.toLocaleString()} icon={BlockRoundedIcon} accent={tokens.brand.main} delta="tenant-scoped" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Monitored Assets" value={activeAssets} icon={DnsRoundedIcon} accent={tokens.severity.info} delta="active assets" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Open Vulnerabilities" value={openVulnerabilities} icon={BugReportRoundedIcon} accent={tokens.severity.medium} delta={`${criticalOpenVulnerabilities} critical`} deltaGood={criticalOpenVulnerabilities === 0} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={5}>
          <SecurityScoreGauge score={score} />
        </Grid>
        <Grid item xs={12} md={7}>
          <SeverityBreakdown data={severityBreakdown} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Box sx={{ mb: 2.5 }}>
            <ThreatTimeline data={timeline} />
          </Box>
          <IncidentsPanel incidents={incidents} />
        </Grid>
        <Grid item xs={12} md={5}>
          <LiveAttackFeed feed={liveFeed} />
        </Grid>
      </Grid>
    </Box>
  );
}
